/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * SoonKi Min (skmin@rsupport.com)
 *
 * @description
 *
 * @created at Tue Dec 19 2023
 **/

import { useEffect, useRef, useState, MouseEvent } from "react";
import { timer } from "rxjs";
import styled from "@emotion/styled";
import { useMutation } from "@tanstack/react-query";
import { isEmpty, uniqBy } from "lodash-es";
import { useSetAtom } from "jotai";

import { ZIndex } from "@/frameworks/core/commons";
import { useUnityHandler } from "@/frameworks/core/unity";
import { useFullSizeChatManager } from "@/frameworks/core/hooks";
import { _T } from "@rfice/language";
import { useAudioZone } from "@/frameworks/core/API";
import { useOutsideClickEffect } from "@/frameworks/libraries/hooks";
import { SceneType } from "@/frameworks/libraries/commons";
import { RestAPIError } from "@/frameworks/libraries/restservice";
import { Each, Maybe } from "@/frameworks/libraries/components";
import { Column, Icon, Row, Typography, TypographyTooltip, Button } from "@/frameworks/designStuff/components";

import * as S from "./emotion";
import { useChatDataStore } from "@/modules/chat/store";
import { useShallow } from "zustand/react/shallow";
import { listFileShare3DAtom } from "@/modules/filebox/components/Files/FileItem/private/FileCard";
import { useChatRoomActions } from "@/frameworks/core/chat";

type ChatRoomListProps = {
	isMinimizeSpaceChat?: boolean;
	isNewMessage?: boolean;
	onSelectChannel: (roomID: string) => void;
	toggleMinimizeSpaceChat?: () => void;
	generalRoomID: string;
	spaceInfo?: IPC.Spaces.EnterSpace;
	channelInfo?: WebServices.SpaceChat.List.Data[number];
};

interface OpenWorldChatRoomListProps extends ChatRoomListProps {
	isOpenWorld: boolean;
	openWorldName: string;
}

function isOpenWorldChatRoomList(
	props: ChatRoomListProps | OpenWorldChatRoomListProps
): props is OpenWorldChatRoomListProps {
	return (props as OpenWorldChatRoomListProps).isOpenWorld;
}

export default function ChatRoomList(props: ChatRoomListProps | OpenWorldChatRoomListProps) {
	const {
		isMinimizeSpaceChat,
		isNewMessage,
		onSelectChannel,
		toggleMinimizeSpaceChat,
		generalRoomID,
		channelInfo,
		spaceInfo
	} = props;
	const isOpenWorld = isOpenWorldChatRoomList(props);
	const generalRoomName = isOpenWorld ? props.openWorldName : channelInfo?.rooms[0].roomName;
	const [showRoomList, setShowRoomList] = useState(false);
	const audioZoneJoinTrialCount = useRef(0);
	const audioZoneIDRef = useRef<string | undefined>(undefined);
	const chatRoomActions = useChatRoomActions();
	const spaceChatRoom = useChatDataStore(
		useShallow((state) => ({
			isAudioZone: state.space_is_audio_zone,
			roomName: state.space_room_name,
			roomId: state.space_room_id
		}))
	);
	const { changeRoom, exitSpace } = useChatDataStore(
		useShallow((state) => ({
			changeRoom: state.change_room,
			exitSpace: state.exit_space
		}))
	);
	const setListFileShare3D = useSetAtom(listFileShare3DAtom);

	const { fullSizeChatActions, useFullSizeChatState } = useFullSizeChatManager();
	const fullSizeChatState = useFullSizeChatState();
	const audioZoneAPI = useAudioZone();

	const { ref: outsideClickRef } = useOutsideClickEffect(() => {
		setShowRoomList(false);
	});

	const retryAudioZoneChatJoin = (audioZoneId: string) => {
		timer(1000).subscribe(() => {
			if (audioZoneJoinTrialCount.current === 5) return;
			audioZoneJoinTrialCount.current += 1;
			joinAudioZoneChat({ audioZoneId });
		});
	};

	const leaveAudioZoneChatRoom = () => {
		audioZoneIDRef.current && chatRoomActions.common.leave(audioZoneIDRef.current);
		onSelectChannel(generalRoomID);
		audioZoneIDRef.current = undefined;
		changeRoom(generalRoomID, generalRoomName || "", false);
		setListFileShare3D([]);
	};

	const changeRoomToAudioZone = (roomID: string) => {
		onSelectChannel(roomID);
		audioZoneIDRef.current = roomID;
		changeRoom(roomID, _T("오디오챗"), true);
	};

	const { mutate: joinAudioZoneChat } = useMutation({
		mutationKey: ["joinAudioZoneChat"],
		mutationFn: async ({ audioZoneId }: { audioZoneId: string }) => {
			return audioZoneAPI.joinChat.execute({ audioZoneId });
		},
		onSuccess({ data }) {
			audioZoneJoinTrialCount.current = 0;
			if (data.roomId) {
				changeRoomToAudioZone(data.roomId);
			}
		},
		onError(_error, variables) {
			const error = _error as RestAPIError;
			if (error.code === "49504") {
				retryAudioZoneChatJoin(variables.audioZoneId);
			}
		}
	});

	const { mutate: createAudioZoneChat } = useMutation({
		mutationKey: ["createAudioZoneChat"],
		mutationFn: async ({ audioZoneId }: { audioZoneId: string }) => {
			return audioZoneAPI.createChat.execute({ audioZoneId });
		},
		onSuccess({ data }) {
			if (data?.id) {
				changeRoomToAudioZone(data.id);
			}
		}
	});

	useUnityHandler(
		"ChatRoomList",
		{
			onChangeAudioZoneState(payload) {
				if (!payload.isAudioZone) {
					leaveAudioZoneChatRoom();
					return;
				} else {
					if (!isEmpty(fullSizeChatState)) {
						fullSizeChatActions.hide();
					}
				}

				if (!payload.audioZoneId || channelInfo?.myRoom) return;

				if (payload.audioZoneOwner) {
					createAudioZoneChat({ audioZoneId: payload.audioZoneId });
				} else {
					joinAudioZoneChat({ audioZoneId: payload.audioZoneId });
				}
			}
		},
		[fullSizeChatState]
	);

	const handleClickChannel = (ev: React.MouseEvent<HTMLLIElement>) => {
		const target = ev.currentTarget as HTMLLIElement;
		const roomID = target.dataset["id"] as string;
		const roomName = target.dataset["name"] as string;
		onSelectChannel(roomID);
		setShowRoomList(false);
		changeRoom(roomID, roomName, roomID === audioZoneIDRef.current);
	};

	useEffect(() => {
		changeRoom(generalRoomID, generalRoomName || "", false);
		return () => {
			exitSpace();
		};
	}, [generalRoomName]);

	const handleMinimizeSpaceChat = (ev: MouseEvent<HTMLButtonElement>) => {
		ev.stopPropagation();

		toggleMinimizeSpaceChat?.();
	};

	return (
		<Column
			ref={outsideClickRef}
			css={{
				width: isMinimizeSpaceChat ? 180 : "calc(100% - 8px)",
				marginLeft: isMinimizeSpaceChat ? 90 : 0,
				position: "relative"
			}}
		>
			<Maybe test={isNewMessage && isMinimizeSpaceChat}>
				<S.BadgeStyled />
			</Maybe>
			<S.ChatRoomSelectButton
				isAudioZone={Boolean(spaceChatRoom?.isAudioZone)}
				isMeeting={spaceInfo?.meetingType === "CONFERENCE"}
				onClick={() => {
					if (spaceInfo?.meetingType === "CONFERENCE") return;
					setShowRoomList(!showRoomList);
				}}
			>
				<TypographyTooltip.button
					tooltipProps={{ hasIcon: false }}
					componentProps={{
						maxLine: 1,
						ellipsis: true,
						style: {
							marginLeft: "4px",
							maxWidth: "250px",
							cursor: "pointer",
							textShadow: "0px 1px 4px rgba(0, 0, 0, 0.5)"
						},
						align: "left",
						stretch: true,
						typoSize: "Title1_B",
						typoColor: "neutral_00_100"
					}}
				>
					{spaceInfo?.sceneType === SceneType.MyRoomScene || spaceInfo?.meetingType === "CONFERENCE"
						? "General"
						: spaceChatRoom?.roomName}{" "}
				</TypographyTooltip.button>
				<Maybe test={!isMinimizeSpaceChat && spaceInfo?.meetingType !== "CONFERENCE"}>
					<Icon id="change-channel" name="UpdownFilled16" size={20} css={{ marginLeft: 8, color: "#fff" }} />
					<S.VerticalDividerStyled />
				</Maybe>{" "}
				<Button.Icon
					id="change-size"
					name={isMinimizeSpaceChat ? "RestoreSizeUp14" : "RestoreSize14"}
					size="small"
					filled="transparent"
					fill="neutral_00_100"
					css={{ minWidth: 28 }}
					onClick={(ev) => handleMinimizeSpaceChat(ev)}
				/>
			</S.ChatRoomSelectButton>
			{showRoomList && (
				<List>
					{audioZoneIDRef.current && (
						<ChatRoom
							data-id={audioZoneIDRef.current}
							data-name={_T("오디오챗")}
							onClick={handleClickChannel}
						>
							<Typography
								typoSize="Title1_B"
								typoColor="accent_20_100"
								css={{ textShadow: "0px 1px 4px rgba(0, 0, 0, 0.5)" }}
							>
								{_T("오디오챗")}
							</Typography>
						</ChatRoom>
					)}
					{isOpenWorld && (
						<ChatRoom data-id={generalRoomID} data-name={props.openWorldName} onClick={handleClickChannel}>
							<Typography
								typoSize="Title1_B"
								typoColor="neutral_00_100"
								css={{ textShadow: "0px 1px 4px rgba(0, 0, 0, 0.5)" }}
							>
								{props.openWorldName}
							</Typography>
						</ChatRoom>
					)}
					<Maybe test={spaceInfo?.meetingType === "CONFERENCE"}>
						<ChatRoom data-id={generalRoomID} data-name={_T("General")} onClick={handleClickChannel}>
							<Typography
								typoSize="Title1_B"
								typoColor="neutral_00_100"
								css={{ textShadow: "0px 1px 4px rgba(0, 0, 0, 0.5)" }}
							>
								{_T("General")}
							</Typography>
						</ChatRoom>
					</Maybe>
					<Each
						of={uniqBy(channelInfo?.rooms, (v) => v.roomId)}
						render={(room) => {
							return (
								<ChatRoom
									key={room.roomId}
									data-id={room.roomId}
									data-name={room.roomName || ""}
									onClick={handleClickChannel}
								>
									<Typography typoSize="Title1_B" typoColor="neutral_00_100">
										{room.roomName}
									</Typography>
								</ChatRoom>
							);
						}}
					/>
				</List>
			)}
		</Column>
	);
}

const List = styled(Column.ul)`
	position: absolute;
	width: 100%;
	bottom: 50px;
	padding: 8px;
	border-radius: 8px;
	background: ${({ theme }) => theme.colors.neutral_80_80};
	backdrop-filter: blur(10px);
	z-index: ${ZIndex.UNITY + 1};
`;

const ChatRoom = styled(Row.li)`
	width: 100%;
	padding: 6px 8px;
	border-radius: 8px;
	cursor: pointer;
	&:hover {
		background: ${({ theme }) => theme.colors.neutral_00_16};
	}
`;
